import React from 'react';
import {
  compose,
  withProps,
  setDisplayName,
} from 'recompose';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import Badge, { Kind as BadgeKind } from '@ux/badge';
import { Content } from '@ux/typeface';
import ExactMatchPurchase, { Against as PurchaseAgainst } from './Purchase';
import DomainLegal from '../domain/Legal';
import Bundle from './Bundle';
import { AvailabilityStatus } from '../../../core/constants';
import { withStyles } from '@ux/style-utils';
import stylesheet from './Available.stylesheet';
import { hasLength, isTrue } from '@team-griffin/capra';
import * as r from 'ramda';

export { AvailabilityStatus };

export const PureExactMatchAvailable = ({
  theme,
  availability,
  fqdn,
  price,
  legalMessages,
  bundleDomains,
  bundleOptions,
  styles,
  isMinMd,
  isPromo,
  oldPrice,
  currency,
  showPrice,
  showBundles,
  bundleAvailability,
  handlePurchaseDomainClick,
  onPurchaseBundleClick,
  onViewBasketClick,
}) => (
  <Content
    component={'div'}
    theme={theme}
  >
    <div style={styles.root}>
      <div
        style={styles.body}
        data-test-id="body"
      >
        <div style={styles.content}>
          <div style={styles.info}>
            <strong
              data-domain="true"
              style={styles.fqdn}
            >
              {fqdn}
            </strong>
            <If condition={isPromo && isMinMd}>
              <Badge
                theme={theme}
                kind={BadgeKind.HIGHLIGHT}
              >
                <FormattedMessage id="dst.exactMatch.v2.promo"/>
              </Badge>
            </If>
            <div style={styles.text}>
              <FormattedMessage
                id="dst.exactMatch.v2.found.text"
                values={{ domain: fqdn }}
              />
            </div>
          </div>
          <div style={styles.action}>
            <If condition={showPrice}>
              <div style={styles.price}>
                <If condition={isPromo && !(isMinMd)}>
                  <span style={styles.badge}>
                    <Badge
                      theme={theme}
                      kind={BadgeKind.HIGHLIGHT}
                    >
                      <FormattedMessage id="dst.exactMatch.v2.promo"/>
                    </Badge>
                  </span>
                </If>
                <FormattedMessage
                  id={`dst.exactMatch.v2.${isPromo ? 'promo.price' : 'price'}`}
                  values={{
                    price: (
                      <strong>
                        <FormattedNumber
                          value={price}
                          // eslint-disable-next-line react/style-prop-object
                          style="currency"
                          currency={currency}
                        />
                      </strong>
                    ),
                  }}
                />
                <If condition={isPromo}>
                  <span style={styles.oldPrice}>
                    <FormattedMessage
                      id="dst.exactMatch.v2.oldPrice"
                      values={{
                        price: (
                          <FormattedNumber
                            value={oldPrice}
                            // eslint-disable-next-line react/style-prop-object
                            style="currency"
                            currency={currency}
                          />
                        ),
                      }}
                    />
                  </span>
                </If>
              </div>
            </If>
            <div style={styles.button}>
              <ExactMatchPurchase
                theme={theme}
                fqdn={fqdn}
                onPurchaseDomainClick={handlePurchaseDomainClick}
                onViewBasketClick={onViewBasketClick}
                intlNamespace={'exactMatch.v2'}
                status={availability}
                against={PurchaseAgainst.PRIMARY}
              />
            </div>
          </div>
        </div>
        <If condition={showBundles}>
          <div style={styles.legal}>
            <DomainLegal
              theme={theme}
              messages={legalMessages}
            />
          </div>
        </If>
      </div>
      <If condition={showBundles}>
        <div style={styles.bundle}>
          <Bundle
            theme={theme}
            domains={bundleDomains}
            options={bundleOptions}
            currency={currency}
            availability={bundleAvailability}
            legalMessages={legalMessages}
            onViewBasketClick={onViewBasketClick}
            onPurchaseBundleClick={onPurchaseBundleClick}
          />
        </div>
      </If>
    </div>
    <If condition={!(showBundles)}>
      <div style={styles.legal}>
        <DomainLegal
          theme={theme}
          messages={legalMessages}
        />
      </div>
    </If>
  </Content>
);

const isUnverified = r.propEq('availability', AvailabilityStatus.UNVERIFIED);
const isUnavailable = r.propEq('availability', AvailabilityStatus.UNAVAILABLE);
const isUnverifiedOrUnavailable = r.either(isUnverified, isUnavailable);

const hasBundleDomains = r.propSatisfies(hasLength, 'bundleDomains');
const hasBundleEnabled = r.pathSatisfies(
  isTrue,
  [ 'features', 'bundles' ],
);

export const enhance = compose(
  setDisplayName('ExactMatchAvailable'),
  withProps((ownerProps) => ({
    showPrice: isUnverifiedOrUnavailable(ownerProps),
    showBundles: r.both(
      hasBundleDomains,
      hasBundleEnabled,
    )(ownerProps),
  })),
  withStyles(stylesheet),
);

export default enhance(PureExactMatchAvailable);
